import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronRight, Sparkles } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import LeadForm from '@/components/LeadForm';
import { servicesData } from '@/data/servicesData';

const ServicesPage = () => {
  const [selectedService, setSelectedService] = useState('');
  const navigate = useNavigate();

  const handleSubcategoryClick = (subItemPath, subItemName) => {
    setSelectedService(subItemName);
    navigate(subItemPath);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Our Comprehensive Services | Stellars Estates - Digital Growth Solutions</title>
        <meta 
          name="description" 
          content="Explore Stellars Estates full range of growth services: Advertisement, Marketing, Development, SEO, AI Automation, Designing & Editing, Integration, Consultations, and On-Site Services." 
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Our Comprehensive Services | Stellars Estates - Digital Growth Solutions" />
        <meta property="og:description" content="Explore Stellars Estates full range of growth services: Advertisement, Marketing, Development, SEO, AI Automation, Designing & Editing, Integration, Consultations, and On-Site Services." />
        <meta property="og:image" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
        <meta property="og:image:secure_url" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
        <meta property="og:image:alt" content="Stellars Estates Services" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://i.postimg.cc/3NNd39Q1/social-sharing-image-stallars-estates.jpg" />
      </Helmet>

      <div className="min-h-screen bg-[#181F32]">
        <Header />
        
        {/* Hero Section */}
        <section className="bg-[#181F32] text-white py-20 lg:py-24 relative overflow-hidden border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 mb-3"
            >
              <span className="glass-badge">
                <Sparkles size={14} className="inline mr-1" />
                End-To-End Business Scaling
              </span>
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 font-heading tracking-tight"
            >
              Our Growth <span className="text-[#F3453F]">Expertise</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto font-sans leading-relaxed"
            >
              Browse our complete catalog of specialized marketing, tech automation, design, and strategic consulting capabilities.
            </motion.p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16 md:py-24 bg-[#181F32]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {servicesData.map((category) => {
                const CategoryIcon = category.icon;
                return (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="glass-panel p-6 sm:p-8 rounded-2xl border border-white/10 flex flex-col justify-between hover:border-[#F3453F]/40 transition-all duration-300 group"
                  >
                    <div>
                      {/* Category Header */}
                      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-white/10">
                        <div className="p-3 rounded-xl bg-[#F3453F]/15 text-[#F3453F] group-hover:scale-110 transition-transform">
                          <CategoryIcon size={24} />
                        </div>
                        <h2 className="text-xl font-extrabold text-white font-heading group-hover:text-[#F3453F] transition-colors">
                          {category.title}
                        </h2>
                      </div>

                      <p className="text-white/80 text-xs sm:text-sm font-sans leading-relaxed mb-6">
                        {category.description}
                      </p>

                      {/* Subcategory List */}
                      {category.hasSubgroups ? (
                        <div className="space-y-4">
                          {category.groups.map((group) => (
                            <div key={group.name} className="space-y-2">
                              <span className="text-xs font-extrabold text-[#F3453F] uppercase tracking-wider block">
                                {group.name}
                              </span>
                              <div className="space-y-1.5">
                                {group.items.map((subItem) => (
                                  <button
                                    key={subItem.name}
                                    onClick={() => handleSubcategoryClick(subItem.path, subItem.name)}
                                    className="w-full text-left text-sm text-white/90 hover:text-white hover:bg-[#F3453F]/20 px-3 py-2 rounded-lg transition-all flex items-center justify-between group/item border border-white/5 hover:border-[#F3453F]/30"
                                  >
                                    <span className="font-medium">{subItem.name}</span>
                                    <ChevronRight size={16} className="text-white/40 group-hover/item:text-[#F3453F] group-hover/item:translate-x-1 transition-all" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {category.items.map((subItem) => (
                            <button
                              key={subItem.name}
                              onClick={() => handleSubcategoryClick(subItem.path, subItem.name)}
                              className="w-full text-left text-sm text-white/90 hover:text-white hover:bg-[#F3453F]/20 px-3 py-2 rounded-lg transition-all flex items-center justify-between group/item border border-white/5 hover:border-[#F3453F]/30"
                            >
                              <span className="font-medium">{subItem.name}</span>
                              <ChevronRight size={16} className="text-white/40 group-hover/item:text-[#F3453F] group-hover/item:translate-x-1 transition-all" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="mt-8 pt-4 border-t border-white/10 flex items-center justify-between text-xs text-white/70">
                      <span>Click any service for details</span>
                      <ArrowRight size={14} className="text-[#F3453F]" />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Lead Form Section */}
        <div id="contact-form">
          <LeadForm prefilledService={selectedService} />
        </div>

        <Footer />
      </div>
    </>
  );
};

export default ServicesPage;
